const editableSelector =
  'input, textarea, select, [role="textbox"], [contenteditable]:not([contenteditable="false"]), [data-preserve-selection]';

const parentElement = (node: Node): Element | null =>
  node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;

const closestCell = (node: Node): HTMLTableCellElement | null =>
  parentElement(node)?.closest<HTMLTableCellElement>('td, th') ?? null;

const boundaryCell = (
  node: Node,
  offset: number,
  end: boolean,
): HTMLTableCellElement | null => {
  const cell = closestCell(node);
  if (cell) return cell;
  // 整个单元格被选中时，浏览器将范围边界放在 tr 的子节点之间。
  const child = node.childNodes[end ? offset - 1 : offset];
  return child ? closestCell(child) : null;
};

/** 仅限单格；跨列/跨行复制继续交给浏览器保留 TSV 分隔符。 */
export function singleCellRange(range: Range): Range | undefined {
  const start = boundaryCell(range.startContainer, range.startOffset, false);
  const end = boundaryCell(range.endContainer, range.endOffset, true);
  if (!start?.closest('.ant-table') || !end || start.closest(editableSelector))
    return undefined;
  if (start.querySelector('table')) return undefined;
  if (start !== end) {
    // 只越过当前格的右边界、尚未选中下一格文字时，会凭空带出一个 Tab。
    if (start.nextElementSibling !== end || range.endOffset !== 0)
      return undefined;
    const prefix = range.cloneRange();
    prefix.selectNodeContents(end);
    prefix.setEnd(range.endContainer, range.endOffset);
    const fragment = prefix.cloneContents();
    if (
      prefix.toString() !== '' ||
      fragment.querySelector('img, svg, input, textarea, select, br')
    )
      return undefined;
  }
  const content = range.cloneRange();
  content.selectNodeContents(start);
  const result = range.cloneRange();
  if (result.compareBoundaryPoints(Range.START_TO_START, content) < 0)
    result.setStart(content.startContainer, content.startOffset);
  if (result.compareBoundaryPoints(Range.END_TO_END, content) > 0)
    result.setEnd(content.endContainer, content.endOffset);
  return result.collapsed ? undefined : result;
}

const trimRange = (range: Range): Range | undefined => {
  const cell = boundaryCell(range.startContainer, range.startOffset, false);
  if (!cell) return undefined;
  const walker = cell.ownerDocument.createTreeWalker(
    cell,
    NodeFilter.SHOW_TEXT,
  );
  const parts: { node: Text; start: number; end: number }[] = [];
  for (let current = walker.nextNode(); current; current = walker.nextNode()) {
    const node = current as Text;
    if (!range.intersectsNode(node)) continue;
    const start = node === range.startContainer ? range.startOffset : 0;
    const end = node === range.endContainer ? range.endOffset : node.length;
    if (end > start) parts.push({ node, start, end });
  }
  const first = parts.find(({ node, start, end }) =>
    /\S/u.test(node.data.slice(start, end)),
  );
  const last = [...parts]
    .reverse()
    .find(({ node, start, end }) => /\S/u.test(node.data.slice(start, end)));
  if (!first || !last) return undefined;
  const leading = first.node.data.slice(first.start, first.end).match(/^\s*/u);
  const trailing = last.node.data.slice(last.start, last.end).match(/\s*$/u);
  const result = range.cloneRange();
  result.setStart(first.node, first.start + (leading?.[0].length ?? 0));
  result.setEnd(last.node, last.end - (trailing?.[0].length ?? 0));
  return result;
};

const editable = (target: EventTarget | null): boolean =>
  target instanceof Element && Boolean(target.closest(editableSelector));

export function installTableTextSelectionGuard(document: Document): () => void {
  const selection = () => {
    const selected = document.defaultView?.getSelection();
    return selected && selected.rangeCount === 1 && !selected.isCollapsed
      ? selected
      : undefined;
  };
  const onDoubleClick = (event: MouseEvent) => {
    if (
      event.defaultPrevented ||
      editable(event.target) ||
      (event.target instanceof Element &&
        event.target.closest('a, button, [role="button"]'))
    )
      return;
    const selected = selection();
    if (!selected) return;
    const cell = singleCellRange(selected.getRangeAt(0));
    const trimmed = cell && trimRange(cell);
    if (!trimmed) return;
    selected.removeAllRanges();
    selected.addRange(trimmed);
  };
  const onCopy = (event: ClipboardEvent) => {
    if (
      event.defaultPrevented ||
      !event.clipboardData ||
      editable(event.target) ||
      editable(document.activeElement)
    )
      return;
    const selected = selection();
    if (!selected || !singleCellRange(selected.getRangeAt(0))) return;
    // 仅使用当前可见选区，不读接口原文/隐藏节点；保留中间空格及多行结构。
    const text = selected.toString().trim();
    if (!text) return;
    event.clipboardData.setData('text/plain', text);
    event.preventDefault();
  };
  document.addEventListener('dblclick', onDoubleClick);
  document.addEventListener('copy', onCopy);
  return () => {
    document.removeEventListener('dblclick', onDoubleClick);
    document.removeEventListener('copy', onCopy);
  };
}
